import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Field, Form, Formik } from 'formik';
import { produce } from 'immer';
import React, { useState } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import LoadingScreen from '@sophocles/components/LoadingScreen';
import { createSiteMaintenanceLog } from '@sophocles/entities/SiteMaintenanceLog';
import { formatDateForSubmission } from '@sophocles/utils/formatUtils';

function AddMaintenanceLogDialog(props) {
  const { user, closeDialog, open, site, onResult, startLoading, endLoading } = props;
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleSubmit = values => {
    setIsLoading(true);
    const submitValues = produce(values, draft => {
      draft.entered_by = user.get('id', 0);
      draft.site = site.get('id', 0);
      draft.date_reported = formatDateForSubmission(draft.date_reported);
      return draft;
    });

    if (submitValues.site !== 0 && submitValues.entered_by !== 0) {
      if (typeof startLoading === 'function') {
        startLoading();
      }
      dispatch(createSiteMaintenanceLog(submitValues)).then(res => {
        if (typeof onResult === 'function') {
          onResult(res);
        }
        if (typeof endLoading === 'function') {
          endLoading();
        }
      });
    }

    setIsLoading(false);
    closeDialog();
  };

  const fullName = user.get('first_name', '') + ' ' + user.get('last_name', '');
  const dateFormat = user?.toJS()?.options?.date_format ?? 'yyyy-MM-dd';

  return (
    <Formik
      initialValues={{
        date_reported: new Date(),
        issue: '',
      }}
      onSubmit={handleSubmit}
    >
      {({
        values,
        setFieldValue,
        handleChange,
        submitForm,
        resetForm,
        isSubmitting,
        touched,
        errors,
        dirty,
        isValid,
      }) => (
        <Dialog open={open} onClose={closeDialog}>
          <DialogTitle>{t('sites.newMaintenanceEntry')}</DialogTitle>
          <DialogContent dividers>
            <Form>
              {isLoading && <LoadingScreen />}
              <div style={{ fontWeight: 'bold', marginBottom: '16px' }}>
                <span>{fullName}</span>
              </div>
              <DatePicker
                name="date_reported"
                label={t('sites.dateOfTheIssue')}
                format={dateFormat}
                sx={{ my: 2 }}
                value={values.date_reported}
                onChange={v => {
                  setFieldValue('date_reported', v);
                }}
                slotProps={{ textField: { size: 'small' } }}
              />
              <Field name="issue">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    label={t('sites.issueDescription')}
                    rows={1}
                    sx={{ my: 2 }}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error ? meta.error : ''}
                  />
                )}
              </Field>
            </Form>
          </DialogContent>
          <DialogActions>
            <Button key="cancel" variant="outlined" disabled={isLoading} onClick={closeDialog}>
              {t('actions.cancel')}
            </Button>
            <Button key="submit" variant="contained" disabled={isLoading || !(isValid && dirty)} onClick={submitForm}>
              {t('actions.submit')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  );
}

export default AddMaintenanceLogDialog;
